/**
 * Climate Zones and Design Conditions for Manual J Calculations
 * Based on ACCA Manual J methodology
 */

// Climate zones with typical design conditions
export const CLIMATE_ZONES = [
  { value: '1A', label: 'Zone 1A - Very Hot Humid (Miami)', heating: 55, cooling: 93, htd: 200, ctd: 21 },
  { value: '2A', label: 'Zone 2A - Hot Humid (Houston)', heating: 35, cooling: 96, htd: 1500, ctd: 21 },
  { value: '2B', label: 'Zone 2B - Hot Dry (Phoenix)', heating: 40, cooling: 108, htd: 1000, ctd: 30 },
  { value: '3A', label: 'Zone 3A - Warm Humid (Atlanta)', heating: 25, cooling: 94, htd: 2800, ctd: 19 },
  { value: '3B', label: 'Zone 3B - Warm Dry (Los Angeles)', heating: 40, cooling: 89, htd: 1500, ctd: 15 },
  { value: '3C', label: 'Zone 3C - Warm Marine (San Francisco)', heating: 40, cooling: 80, htd: 2500, ctd: 10 },
  { value: '4A', label: 'Zone 4A - Mixed Humid (DC/Baltimore)', heating: 15, cooling: 93, htd: 4500, ctd: 18 },
  { value: '4B', label: 'Zone 4B - Mixed Dry (Albuquerque)', heating: 15, cooling: 96, htd: 4200, ctd: 26 },
  { value: '4C', label: 'Zone 4C - Mixed Marine (Seattle)', heating: 25, cooling: 84, htd: 4800, ctd: 14 },
  { value: '5A', label: 'Zone 5A - Cool Humid (Chicago)', heating: -5, cooling: 91, htd: 6200, ctd: 17 },
  { value: '5B', label: 'Zone 5B - Cool Dry (Denver)', heating: 0, cooling: 93, htd: 6000, ctd: 25 },
  { value: '6A', label: 'Zone 6A - Cold Humid (Minneapolis)', heating: -15, cooling: 89, htd: 7800, ctd: 16 },
  { value: '6B', label: 'Zone 6B - Cold Dry (Helena)', heating: -15, cooling: 88, htd: 7500, ctd: 22 },
  { value: '7', label: 'Zone 7 - Very Cold (Duluth)', heating: -25, cooling: 83, htd: 9000, ctd: 14 },
  { value: '8', label: 'Zone 8 - Subarctic (Fairbanks)', heating: -45, cooling: 78, htd: 13000, ctd: 12 },
];

// Indoor design conditions
export const INDOOR_CONDITIONS = {
  heating: 70, // °F indoor temp for heating
  cooling: 75, // °F indoor temp for cooling
  humidity: 50, // % relative humidity
};

// Default design conditions (can be overridden by user)
export const DEFAULT_DESIGN_CONDITIONS = {
  climateZone: '4A',
  heatingOutdoor: 15, // °F
  coolingOutdoor: 93, // °F
  indoorHeating: 70, // °F
  indoorCooling: 75, // °F
  latitude: 39, // degrees
  altitude: 500, // feet
  dailyRange: 'medium', // low, medium, high
};

// Daily temperature range classifications
export const DAILY_RANGE = [
  { value: 'low', label: 'Low (< 15°F)', factor: 1.0 },
  { value: 'medium', label: 'Medium (15-25°F)', factor: 0.85 },
  { value: 'high', label: 'High (> 25°F)', factor: 0.7 },
];

// Duct location factors
export const DUCT_LOCATIONS = [
  { value: 'conditioned', label: 'In conditioned space', heatingFactor: 1.0, coolingFactor: 1.0 },
  { value: 'attic-insulated', label: 'Attic (insulated)', heatingFactor: 1.1, coolingFactor: 1.15 },
  { value: 'attic-uninsulated', label: 'Attic (uninsulated)', heatingFactor: 1.25, coolingFactor: 1.35 },
  { value: 'crawlspace', label: 'Crawlspace', heatingFactor: 1.1, coolingFactor: 1.1 },
  { value: 'basement-unconditioned', label: 'Basement (unconditioned)', heatingFactor: 1.05, coolingFactor: 1.05 },
];

// Infiltration levels
export const INFILTRATION_LEVELS = [
  { value: 'tight', label: 'Tight (new construction)', ach: 0.25 },
  { value: 'average', label: 'Average', ach: 0.5 },
  { value: 'loose', label: 'Loose (older home)', ach: 0.75 },
  { value: 'leaky', label: 'Leaky', ach: 1.0 },
];

// Occupancy load factors
export const OCCUPANCY = {
  sensiblePerPerson: 230, // BTU/hr sensible heat per person
  latentPerPerson: 200, // BTU/hr latent heat per person
  defaultOccupants: 2, // default # of people
};

// Internal gains by room type (BTU/hr)
export const INTERNAL_GAINS = {
  'living-room': { sensible: 400, latent: 0 },
  'family-room': { sensible: 500, latent: 0 },
  'master-bedroom': { sensible: 200, latent: 0 },
  'bedroom': { sensible: 150, latent: 0 },
  'kitchen': { sensible: 1200, latent: 400 },
  'dining-room': { sensible: 200, latent: 0 },
  'bathroom': { sensible: 100, latent: 200 },
  'laundry': { sensible: 500, latent: 300 },
  'office': { sensible: 400, latent: 0 },
  'bonus-room': { sensible: 300, latent: 0 },
  'garage': { sensible: 0, latent: 0 },
  'other': { sensible: 200, latent: 0 },
};

// Quick estimate BTU factors per sq ft (simplified rule-of-thumb)
// Based on industry standard of approximately 1 ton per 500-600 sq ft
// which equals ~20-24 BTU/sq ft for average conditions
export const QUICK_ESTIMATE_FACTORS = {
  // BTU per sq ft for cooling (adjusted to match ~550 sq ft/ton rule)
  cooling: {
    '1A': 28, '2A': 26, '2B': 28, '3A': 24, '3B': 22, '3C': 18,
    '4A': 22, '4B': 24, '4C': 18, '5A': 20, '5B': 22, '6A': 18,
    '6B': 20, '7': 16, '8': 14,
  },
  // BTU per sq ft for heating
  heating: {
    '1A': 15, '2A': 20, '2B': 18, '3A': 28, '3B': 22, '3C': 25,
    '4A': 35, '4B': 32, '4C': 30, '5A': 45, '5B': 42, '6A': 55,
    '6B': 52, '7': 65, '8': 80,
  },
};

// Adjustment factors for quick estimates
export const QUICK_ESTIMATE_ADJUSTMENTS = {
  ceilingHeight: {
    // Factor per foot above 8ft
    perFoot: 0.04,
  },
  windows: {
    // Add % for large window area
    largeWindowPenalty: 0.15,
  },
  insulation: {
    poor: 1.2,
    average: 1.0,
    good: 0.9,
    excellent: 0.8,
  },
  sunExposure: {
    shaded: 0.9,
    partial: 1.0,
    full: 1.1,
  },
};
